(function () {
  'use strict';

  // Configuration
  const PRICE_SELECTORS = [
    '.murphy-door-configurator__price-group', // Configurator price
    '.pdp-sticky-bar', // Standard product sticky bottom bar
  ];

  const FLASH_CLASS = 'price-flash';
  const DEBOUNCE_DELAY = 150; // ms - prevents animation spam during rapid updates

  // Store last known prices to detect actual value changes
  const priceCache = new WeakMap();

  function extractPriceValue(element) {
    const text = element.textContent || '';
    // Extract all numbers (including decimals) and join them
    const matches = text.match(/[\d,]+\.?\d*/g);
    if (!matches) return null;

    // Take the first significant number (usually the actual price)
    return (
      matches.map((m) => parseFloat(m.replace(/,/g, ''))).find((n) => n > 0) ||
      null
    );
  }

  function triggerFlash(container) {
    // Remove class if it exists (force animation restart)
    container.classList.remove(FLASH_CLASS);

    // Force reflow to restart CSS animation
    void container.offsetWidth;

    // Add flash class to trigger animation
    container.classList.add(FLASH_CLASS);

    // Clean up after animation completes
    container.addEventListener(
      'animationend',
      function cleanup() {
        container.classList.remove(FLASH_CLASS);
        container.removeEventListener('animationend', cleanup);
      },
      { once: true }
    );
  }

  function debounce(fn, delay) {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function handlePriceChange(container) {
    const currentPrice = extractPriceValue(container);
    const cachedPrice = priceCache.get(container);

    // Only trigger flash if price actually changed (not just DOM mutation)
    if (
      currentPrice !== null &&
      cachedPrice !== undefined &&
      currentPrice !== cachedPrice
    ) {
      triggerFlash(container);
    }

    // Update cache with new price
    if (currentPrice !== null) {
      priceCache.set(container, currentPrice);
    }
  }

  // Debounced change handler
  const debouncedHandleChange = debounce(handlePriceChange, DEBOUNCE_DELAY);

  function observePriceContainer(container) {
    // Initialize cache with current price
    const initialPrice = extractPriceValue(container);
    if (initialPrice !== null) {
      priceCache.set(container, initialPrice);
    }

    // Create observer for this container
    const observer = new MutationObserver((mutations) => {
      // Check if any mutation affected text content or child elements
      const hasRelevantChanges = mutations.some(
        (mutation) =>
          mutation.type === 'characterData' ||
          mutation.type === 'childList' ||
          mutation.type === 'attributes' // This is sufficient due to attributeFilter
      );

      if (hasRelevantChanges) {
        debouncedHandleChange(container);
      }
    });

    // Observe the container and all its descendants
    observer.observe(container, {
      childList: true, // Watch for added/removed children
      characterData: true, // Watch for text content changes
      subtree: true, // Watch all descendants
      attributes: true, // Watch for attribute changes (like hiding/showing)
      attributeFilter: ['class', 'style'], // Only watch relevant attributes
    });

    return observer;
  }

  function initializePriceObservers() {
    const observers = [];

    PRICE_SELECTORS.forEach((selector) => {
      const containers = document.querySelectorAll(selector);

      containers.forEach((container) => {
        const observer = observePriceContainer(container);
        observers.push(observer);
      });
    });

    return observers;
  }

  function watchForDynamicContainers() {
    // Target #MainContent instead of document.body to reduce observer triggers
    let mainContent = document.getElementById('MainContent');
    if (!mainContent) {
      console.warn(
        'Price flash observer: #MainContent element not found. Falling back to observing document.body.'
      );
      mainContent = document.body; // Fallback
    }

    const bodyObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          // Only process element nodes
          if (node.nodeType !== 1) return;

          // Check if the added node itself is a price container
          PRICE_SELECTORS.forEach((selector) => {
            if (node.matches && node.matches(selector)) {
              observePriceContainer(node);
            } else if (node.querySelectorAll) {
              // Check for price containers within the added node
              const containers = node.querySelectorAll(selector);
              containers.forEach((container) =>
                observePriceContainer(container)
              );
            }
          });
        });
      });
    });

    // Watch the main content area for dynamically added elements
    bodyObserver.observe(mainContent, {
      childList: true,
      subtree: true,
    });

    return bodyObserver;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializePriceObservers();
      watchForDynamicContainers();
    });
  } else {
    // DOM already loaded
    initializePriceObservers();
    watchForDynamicContainers();
  }
})();
